import { Button } from '@components/button';
import { useModal } from '@components/modal';
import { Typography } from '@components/typography';
import { Stack } from '@mui/material';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useKeyPressEvent } from 'react-use';

type ConfirmationModalProps = {
  message?: string;
  content?: ReactNode;
  affirmText?: string;
  onAffirm?: () => void | Promise<unknown>;
  onReject?: () => void | Promise<unknown>;
  variant?: 'danger' | 'default';
};

export const ConfirmationModal = ({
  message,
  content,
  affirmText,
  variant = 'default',
  onAffirm,
  onClose,
  onReject,
}: ConfirmationModalProps & { onClose: () => void }) => {
  const handleAffirm = () => {
    if (onAffirm) {
      void onAffirm();
    }
    if (onClose) {
      onClose();
    }
  };

  const handleReject = () => {
    if (onReject) {
      void onReject();
    }
    if (onClose) {
      onClose();
    }
  };

  useKeyPressEvent('Enter', (keyboardEvent: KeyboardEvent & { target: { localName: string } }) => {
    if (keyboardEvent.target.localName !== 'div') {
      return;
    }
    handleAffirm();
  });

  return (
    <Stack
      data-cy="Confirmation-Modal"
      direction="column"
      minWidth="516px"
      maxWidth="650px"
      height="auto"
      data-testid="confirmationDiv"
    >
      {content ?? (
        <Typography
          variant="subtitle1"
          data-cy="Confirmation-Modal-Message"
          sx={{ mt: 6, mb: 9, textAlign: 'center' }}
        >
          {message || 'Are you sure?'}
        </Typography>
      )}
      <Button
        fullWidth
        onClick={handleAffirm}
        data-cy="Confirmation-Modal-Delete-Button"
        className="affirm-button"
        sx={{
          mb: 4,
          ...(variant === 'danger' && {
            '&:hover': { backgroundColor: 'error.main', borderColor: 'error.dark' },
          }),
        }}
        autoFocus
      >
        {affirmText || 'Yes'}
      </Button>
      <Button
        fullWidth
        onClick={handleReject}
        data-cy="Confirmation-Modal-Cancel-Button"
        variant="secondary"
        className="reject-button"
      >
        Cancel
      </Button>
    </Stack>
  );
};

export const useConfirmation = (props?: ConfirmationModalProps) => {
  const { showModal } = useModal({
    component: ConfirmationModal,
    componentProps: props,
  });
  const showConfirmation = useCallback(
    (confirmationProps?: ConfirmationModalProps) =>
      showModal({ componentProps: confirmationProps }),
    [showModal],
  );
  return useMemo(() => ({ showConfirmation }), [showConfirmation]);
};
